import api from 'api/api';

type ConnectionStatus = 'connected' | 'connecting' | 'disconnected';

class WebSocketServiceClass {
  private ws: WebSocket | null = null;
  private connectionPromise: Promise<void> | null = null;
  private reconnectDelay = 10000;

  private connectionStatus: ConnectionStatus = 'disconnected';

  // topic -> handlers (multiple screens can listen to the same topic)
  private subscriptions: Map<string, Set<(data: any) => void>> = new Map();

  // queued subs before WebSocket connects
  private queuedSubscriptions: { topic: string; handler: (data: any) => void }[] = [];

  // To track active subscribed topics (for reconnect)
  private activeTopics: Set<string> = new Set();

  public getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  // ==========================
  // 🔥 Native WebSocket Connect
  // ==========================
  public connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.connectionStatus = 'connected';
      return Promise.resolve();
    }

    if (this.connectionPromise) return this.connectionPromise;

    this.connectionStatus = 'connecting';

    this.connectionPromise = new Promise((resolve) => {
      const wsUrl = api.getWebSocketAPI();

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('>> WebSocket connected');
        this.connectionStatus = 'connected';

        // Process subs queued before connection was open
        this.processQueuedSubscriptions();

        // 🔥 Re-send subscriptions after reconnect
        this.activeTopics.forEach((topic) => {
          this.sendSubscribe(topic);
          console.log('Re-subscribed to:', topic);
        });

        resolve();
      };

      this.ws.onerror = (err) => {
        console.error('WS Error:', err);
      };

      this.ws.onclose = () => {
        console.warn('>> WS closed');
        this.connectionStatus = 'disconnected';
        this.connectionPromise = null;
        this.reconnect();
      };

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          const { topic, data } = msg;

          const handlers = this.subscriptions.get(topic);
          handlers?.forEach((handler) => handler(data));
        } catch (err) {
          console.error('WS message parse error:', err);
        }
      };
    });

    return this.connectionPromise;
  }

  // ==========================
  // 🔥 Reconnect Logic
  // ==========================
  private reconnect(): void {
    console.log(`>> Reconnecting in ${this.reconnectDelay / 1000}s`);
    setTimeout(() => {
      this.connect().catch((e) => console.error('Reconnect FAILED:', e));
    }, this.reconnectDelay);
  }

  // ==========================
  // 🔥 Internal send helpers
  // ==========================
  private sendSubscribe(topic: string): void {
    this.ws?.send(
      JSON.stringify({
        action: 'subscribe',
        topic
      })
    );
  }

  private sendUnsubscribe(topic: string): void {
    this.ws?.send(
      JSON.stringify({
        action: 'unsubscribe',
        topic
      })
    );
  }

  // ==========================
  // 🔥 Subscription Handling
  // ==========================
  public subscribe(topic: string, handler: (data: any) => void): void {
    this.addHandler(topic, handler);
    this.activeTopics.add(topic);

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // queue until ws opens
      this.queuedSubscriptions.push({ topic, handler });
      console.log('Queueing subscription:', topic);
      return;
    }

    console.log('Subscribed:', topic);
    this.sendSubscribe(topic);
  }

  private processQueuedSubscriptions(): void {
    this.queuedSubscriptions.forEach(({ topic, handler }) => {
      this.addHandler(topic, handler);
      this.activeTopics.add(topic);
      this.sendSubscribe(topic);
      console.log('Processing queued subscription:', topic);
    });
    this.queuedSubscriptions = [];
  }

  // ==========================
  // 🔥 Unsubscribe
  // ==========================
  // Pass the handler to drop only that listener; without it the whole topic is dropped.
  public unsubscribe(topic: string, handler?: (data: any) => void): void {
    const handlers = this.subscriptions.get(topic);
    if (!handlers) return;

    if (handler) {
      handlers.delete(handler);
      this.queuedSubscriptions = this.queuedSubscriptions.filter((item) => item.topic !== topic || item.handler !== handler);
      if (handlers.size > 0) return;
    } else {
      this.queuedSubscriptions = this.queuedSubscriptions.filter((item) => item.topic !== topic);
    }

    this.subscriptions.delete(topic);
    this.activeTopics.delete(topic);

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendUnsubscribe(topic);
    }

    console.log('Unsubscribed from:', topic);
  }

  private addHandler(topic: string, handler: (data: any) => void): void {
    const handlers = this.subscriptions.get(topic);
    if (handlers) handlers.add(handler);
    else this.subscriptions.set(topic, new Set([handler]));
  }

  public unsubscribeAll(): void {
    this.activeTopics.forEach((topic) => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.sendUnsubscribe(topic);
      }
    });

    this.subscriptions.clear();
    this.activeTopics.clear();
    console.log('Unsubscribed from ALL topics');
  }

  // ==========================
  // 🔥 Disconnect
  // ==========================
  public disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.connectionPromise = null;
      this.connectionStatus = 'disconnected';
      console.log('>> WebSocket disconnected');
    }
  }
}

const WebSocketService = new WebSocketServiceClass();
export default WebSocketService;
